import { array, boolean, lazy, mixed, number, object, string } from 'yup'

import { DataAccessRequestParticipantFileStatus } from 'services/results/enums'
import { buildStudyIdSchema } from 'services/shared/schemas/studyIdSchema'

import {
  DataAccessRequestReviewSubStatus,
  DataAccessRequestSharingStatus,
  DataAccessRequestStatus,
  DataAccessRequestType,
  StudyIdConflictKind,
  StudyIdConflictSource,
  StudyIdEvaluationOutcome
} from './enums'

const dataAccessRequestLabelSchema = object({
  _id: string().required(),
  name: string().required()
}).required()

const dataAccessRequestUserReferenceSchema = object({
  _id: string().required(),
  email: string().required(),
  name: string().optional()
}).unknown(true)

const createOptionalDataAccessRequestUserReferenceSchema = () =>
  lazy((value: unknown) => {
    if (value === undefined || value === null) {
      return mixed().nullable().notRequired()
    }

    return dataAccessRequestUserReferenceSchema
  })

const paginationMetaSchema = object({
  totalCount: number().required(),
  totalPages: number().required(),
  currentPage: number().required(),
  perPage: number().required(),
  nextPage: number().nullable().optional(),
  prevPage: number().nullable().optional()
}).required()

const dataAccessRequestDatasetSchema = object({
  datasetName: string().required(),
  datasourceId: string().required(),
  federatedConnectionId: string().required(),
  sourceOrganisationId: string().required(),
  sourceOrganisationName: string().optional(),
  sourceWorkspaceId: string().required(),
  sourceWorkspaceName: string().optional(),
  databaseServerId: string().optional(),
  datasetColor: string().optional()
})
  .unknown(true)
  .required()

const dataAccessRequestVerificationListItemSchema = object({
  _id: string().required(),
  requestType: mixed<DataAccessRequestType>().oneOf(Object.values(DataAccessRequestType)).required(),
  status: mixed<DataAccessRequestStatus>().oneOf(Object.values(DataAccessRequestStatus)).required(),
  reviewSubStatus: mixed<DataAccessRequestReviewSubStatus>()
    .oneOf(Object.values(DataAccessRequestReviewSubStatus))
    .optional(),
  sourceWorkspaceId: string().required(),
  requesterWorkspaceId: string().required(),
  requesterWorkspaceName: string().optional(),
  requesterOrganisationId: string().required(),
  requesterOrganisationName: string().optional(),
  requestingUser: createOptionalDataAccessRequestUserReferenceSchema(),
  assignedTo: createOptionalDataAccessRequestUserReferenceSchema(),
  labels: array(dataAccessRequestLabelSchema).required(),
  datasetName: string().required(),
  datasourceId: string().required(),
  federatedConnectionId: string().required(),
  studyId: buildStudyIdSchema().notRequired(),
  createdAt: string().required()
})
  .unknown(true)
  .required()

export const dataAccessRequestVerificationDetailSchema = object({
  _id: string().required(),
  requestType: mixed<DataAccessRequestType>().oneOf(Object.values(DataAccessRequestType)).required(),
  requestingUser: createOptionalDataAccessRequestUserReferenceSchema(),
  requesterContext: object({
    organisationId: string().required(),
    organisationName: string().optional(),
    workspaceId: string().required(),
    workspaceName: string().optional()
  })
    .unknown(true)
    .required(),
  formSubmission: lazy((value: unknown) => {
    if (value === undefined || value === null) {
      return mixed().nullable().notRequired()
    }

    return object().unknown(true).required()
  }),
  status: mixed<DataAccessRequestStatus>().oneOf(Object.values(DataAccessRequestStatus)).required(),
  groupId: string().required(),
  dataset: lazy((value: unknown) => {
    if (value === undefined || value === null) {
      return mixed().nullable().notRequired()
    }

    return dataAccessRequestDatasetSchema
  }),
  biobankId: string().optional(),
  omopSchemaName: string().optional(),
  query: object().unknown(true).optional(),
  cohort: object({
    federatedCohortId: string().optional(),
    sourceCohortId: string().optional(),
    numberOfParticipants: number().optional(),
    totalNumberOfParticipants: number().optional()
  })
    .unknown(true)
    .default(undefined)
    .optional(),
  hasParticipantRestrictions: mixed<boolean>().optional(),
  // `.default(undefined)` keeps an absent participantFile absent. Without it yup casts the
  // missing key to a `{}` shaped from this object, which both trips the required `status`
  // below and makes `!participantFile` false for consumers that treat falsy as "not sent".
  participantFile: object({
    status: mixed<DataAccessRequestParticipantFileStatus>()
      .oneOf(Object.values(DataAccessRequestParticipantFileStatus))
      .required()
  })
    .unknown(true)
    .default(undefined)
    .optional(),
  labels: array(dataAccessRequestLabelSchema).required(),
  assignedTo: createOptionalDataAccessRequestUserReferenceSchema(),
  assignedBy: createOptionalDataAccessRequestUserReferenceSchema(),
  assignedAt: string().optional(),
  verifiedBy: createOptionalDataAccessRequestUserReferenceSchema(),
  verifiedAt: string().optional(),
  rejectionReason: string().optional(),
  reviewSubStatus: mixed<DataAccessRequestReviewSubStatus>()
    .oneOf(Object.values(DataAccessRequestReviewSubStatus))
    .optional(),
  parentRequestId: string().optional(),
  additionalInformation: lazy((value: unknown) => {
    if (value === undefined || value === null) {
      return mixed().nullable().notRequired()
    }

    return object({
      submissionRequestId: string().optional(),
      submittedAt: string().optional()
    })
      .unknown(true)
      .required()
  }),
  additionalInformationSubmission: lazy((value: unknown) => {
    if (value === undefined || value === null) {
      return mixed().nullable().notRequired()
    }

    return object().unknown(true).required()
  }),
  failureReason: string().optional(),
  studyId: buildStudyIdSchema().notRequired(),
  dataAccessRequestDetail: object({
    isUnshared: mixed<boolean>().required()
  })
    .unknown(true)
    .default(undefined)
    .optional(),
  // `.default(undefined)` keeps an absent sharing object absent. A missing object means sharing
  // has not started; do not coerce it into a pending lifecycle.
  sharing: object({
    status: mixed<DataAccessRequestSharingStatus>().oneOf(Object.values(DataAccessRequestSharingStatus)).required(),
    targetWorkspaceId: string().required(),
    targetWorkspaceName: string().optional(),
    requestedAt: string().required(),
    finishedAt: string().optional(),
    failureReason: string().optional()
  })
    .unknown(true)
    .default(undefined)
    .optional(),
  createdAt: string().required(),
  updatedAt: string().required()
})
  .unknown(true)
  .required()

export const getDataAccessRequestsVerificationResponseSchema = object({
  data: array(dataAccessRequestVerificationListItemSchema).required(),
  pagination: paginationMetaSchema.required()
})
  .unknown(true)
  .required()

export const getDataAccessRequestLabelsResponseSchema = object({
  data: array(dataAccessRequestLabelSchema).required()
})
  .unknown(true)
  .required()

const dataAccessRequestFilterUserSchema = object({
  _id: string().required(),
  email: string().required()
}).required()

export const getDataAccessRequestFilterUsersResponseSchema = object({
  data: array(dataAccessRequestFilterUserSchema).required()
})
  .unknown(true)
  .required()

export const getDataAccessRequestFilterAssigneesResponseSchema = object({
  data: array(dataAccessRequestFilterUserSchema).required()
})
  .unknown(true)
  .required()

const dataAccessRequestFilterWorkspaceSchema = object({
  _id: string().required(),
  name: string().required()
}).required()

export const getDataAccessRequestFilterWorkspacesResponseSchema = object({
  data: array(dataAccessRequestFilterWorkspaceSchema).required()
})
  .unknown(true)
  .required()

export const getAssignableAssigneesForDataAccessRequestResponseSchema = object({
  data: array(dataAccessRequestFilterUserSchema).required()
})
  .unknown(true)
  .required()

export const getDataAccessRequestVerificationAttachmentSignedUrlResponseSchema = object({
  signedUrl: string().required(),
  fileKey: string().optional()
})
  .unknown(true)
  .required()

export const getDataAccessRequestParticipantFileSignedUrlResponseSchema = object({
  signedUrl: string().required(),
  fileName: string().required()
})
  .unknown(true)
  .required()

const studyIdEvaluationWorkspaceSchema = () =>
  object({
    id: string().required(),
    name: string().required()
  })
    .unknown(true)
    .required()

const groupStudyIdAssignmentSchema = object({
  dataAccessRequestId: string().required(),
  studyId: buildStudyIdSchema().required()
})
  .unknown(true)
  .required()

const groupStudyIdAdvisorySchemaFields = {
  hasGroupStudyIdAssigned: boolean().required(),
  groupStudyIdAssignments: array(groupStudyIdAssignmentSchema).required()
}

export const studyIdEvaluationConflictSchema = object({
  kind: mixed<StudyIdConflictKind>().oneOf(Object.values(StudyIdConflictKind)).required(),
  source: mixed<StudyIdConflictSource>().oneOf(Object.values(StudyIdConflictSource)).required(),
  // `.default(undefined)` keeps an absent workspace absent, as with `participantFile` above.
  workspace: studyIdEvaluationWorkspaceSchema().default(undefined).optional(),
  datasource: object({
    datasourceId: string().required(),
    datasetName: string().required()
  })
    .unknown(true)
    .required(),
  dataAccessRequestId: string().optional(),
  hasParticipantRestrictions: mixed<boolean>().required()
})
  .unknown(true)
  .required()

const studyIdEvaluationMatchedSchema = object({
  outcome: mixed<StudyIdEvaluationOutcome.Matched>().oneOf([StudyIdEvaluationOutcome.Matched]).required(),
  workspace: studyIdEvaluationWorkspaceSchema(),
  ...groupStudyIdAdvisorySchemaFields
})
  .unknown(true)
  .required()

const studyIdEvaluationUnmatchedSchema = object({
  outcome: mixed<StudyIdEvaluationOutcome.Unmatched>().oneOf([StudyIdEvaluationOutcome.Unmatched]).required(),
  ...groupStudyIdAdvisorySchemaFields
})
  .unknown(true)
  .required()

const studyIdEvaluationConflictResultSchema = object({
  outcome: mixed<StudyIdEvaluationOutcome.Conflict>().oneOf([StudyIdEvaluationOutcome.Conflict]).required(),
  conflict: studyIdEvaluationConflictSchema.required(),
  ...groupStudyIdAdvisorySchemaFields
})
  .unknown(true)
  .required()

/**
 * POST `/v1/data-access-requests/verification/{requestId}/study-id-evaluation` response.
 *
 * A discriminated union on `outcome`; `mixed` rather than `lazy` so it stays an `AnySchema`
 * that `validateApiResponse` accepts at the top level.
 */
export const studyIdEvaluationResultSchema = mixed().test(
  'study-id-evaluation-result',
  'Invalid Study ID evaluation result',
  (value: unknown) => {
    if (value === undefined || value === null || typeof value !== 'object') {
      return false
    }

    switch ((value as { outcome?: unknown }).outcome) {
      case StudyIdEvaluationOutcome.Matched:
        return studyIdEvaluationMatchedSchema.isValidSync(value)
      case StudyIdEvaluationOutcome.Unmatched:
        return studyIdEvaluationUnmatchedSchema.isValidSync(value)
      case StudyIdEvaluationOutcome.Conflict:
        return studyIdEvaluationConflictResultSchema.isValidSync(value)
      default:
        return false
    }
  }
)

/** POST `/v1/data-access-requests/verification/{requestId}/study-id-evaluation` body. */
export const evaluateDataAccessRequestStudyIdParamsSchema = object({
  studyId: buildStudyIdSchema().required()
}).required()

/** PUT `/v1/data-access-requests/verification/{requestId}/approve` body. */
export const approveDataAccessRequestParamsSchema = object({
  studyId: buildStudyIdSchema().notRequired()
}).required()
